import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import {
  Box,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  FormControl,
  Divider,
  CircularProgress,
  Chip,
  Autocomplete,
} from "@mui/material";
import { Button, IconButton, TextField } from "@ui";
import ThemeSelect from "@/ui/fields/Select";

import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { FiberManualRecord as DotIcon } from "@mui/icons-material";
import {
  customerEndpoints,
  onboardingEndpoints,
  sharePageEndpoints,
} from "@/shared/api/endpoints";
import {
  AdditionalSupportEntry,
  ApiResponse,
  CalculatedTotals,
  CustomerSharingResponse,
  Debt,
  DigitalLender,
  LenderNotShared,
  NotifyCustomerDebtAdvice,
  NotifyCustomerDebtAdviceResponse,
  ServiceResponse,
  SharedDocResponse,
} from "@/shared/api/types";
import { httpGet, httpPost, httpPut } from "@/shared/api/http";
import femaCapture from "@/shared/fema/capture";
import { BenefitsService, OnboardingService } from "@/shared/api/services";
import { getCustomerId } from "@/shared/lib/utils/getCustomerId";
import { SharePageService } from "@/shared/api/services/sharePageService";
import { findMasterLender, type LenderParentRecord } from "@/shared/api/services/lenderService";
import { formatDate } from "@/shared/lib/utils/formatDate";
import { coerceConsentToHuman } from "@/shared/lib/utils/consent";
import { useAlert } from "@/app/providers/AlertProvider";
import DependentsService, {
  DependentDobRecord,
} from "@/shared/api/services/dependentsService";
import { ThemeColours } from "@/clients/acme/config/colours";
import { calculateAge } from "@/shared/lib/utils/others";
import { getCompanyDocs } from "@/shared/share/sharePageUtils";
import { CORE_ASP_COMPANIES, isCoreAspCompany } from "@/constants/asp";
import { findFreepostByPartialName, isPartialNameMatch } from "@/shared/lib/utils/freepostMatcher";
import { parseTextWithLinks } from "@/components/InfoDrawer";
import SupportDetailsPanel from "./shareCom/SupportDetailsPanel";
import { resolveSupportCompanyLogo } from "./shareCom/supportCompanyLogo";
import { SUPPORT_DETAILS } from "./shareCom/supportDetailsContent";

import type { Company, Document } from "./SharePage";

function isOver65(dobStr?: string): boolean {
  const ageYears = calculateAge(dobStr);
  return typeof ageYears === "number" && ageYears >= 65;
}

function buildQueryString(
  params: { param: string; value: string | number }[],
): string {
  const query = params
    .map(
      ({ param, value }) =>
        `${encodeURIComponent(param)}=${encodeURIComponent(value)}`,
    )
    .join("&");
  return query ? `?${query}` : "";
}

interface AdditionalSupportModalProps {
  onClose: () => void;
  digitalData: Object | undefined;
  ieMstrLenderData: LenderParentRecord[];
  digitalLenders: DigitalLender[];
  summary: CalculatedTotals;
  setCompanies: React.Dispatch<React.SetStateAction<Company[]>>;
  companies: Company[];
  setCompanyShareLastUpdateDate: React.Dispatch<React.SetStateAction<number>>;
  setFreePostLastUpdateDate: React.Dispatch<React.SetStateAction<number>>;
  freepostCreditorNamesList: DigitalLender[];
  onEligibleConditions?: (conditions: string[]) => void;
}

const AdditionalSupportModal: React.FC<AdditionalSupportModalProps> = ({
  onClose,
  digitalData,
  ieMstrLenderData,
  digitalLenders,
  setCompanies,
  companies,
  setCompanyShareLastUpdateDate,
  setFreePostLastUpdateDate,
  freepostCreditorNamesList,
  onEligibleConditions,
}) => {
  const [loadedBenefitsAmount, setLoadedBenefitsAmount] = useState(false);
  const [showENWMessage, setShowENWMessage] = useState(false);
  const [showUKPNMessage, setShowUKPNMessage] = useState(false);

  // MWG state vars
  const [disableMwgBtns, setDisableMwgBtns] = useState(false);
  const [count, setCount] = useState(0);
  const [visible, setVisible] = useState(false);

  const { showAlert } = useAlert();

  const [
    showWestViewAdviceAndResourceCentre,
    setShowWestViewAdviceAndResourceCentre,
  ] = useState(false);
  const [isCitizensAdviceMidLincolnshire, setIsCitizensAdviceMidLincolnshire] =
    useState(false);
  const [isWellnessRelatedPostCode, setIsWellnessRelatedPostCode] =
    useState(false);
  const [isUUWellnessRelatedPostCode, setIsUUWellnessRelatedPostCode] =
    useState(false);

  const [isSLSEligible, setIsSLSEligible] = useState(false);
  const [showCitizensAdvicePlymouth, setShowCitizensAdvicePlymouth] =
    useState(false);
  const [hasMobileNumber, setHasMobileNumber] = useState(false);
  const [supportPartnersNames, setSupportPartnersNames] = useState<string[]>([
    "",
  ]);
  const [postcodeClients, setPostcodeClients] = useState<any[]>([]);
  const [companyArr, setCompanyArr] = useState<string[]>([""]);
  const [creditorNamesList, setCreditorNamesList] = useState<DigitalLender[]>(
    [],
  );

  const [selectedSupportPartners, setSelectedSupportPartners] = useState<
    string[]
  >([]);

  const [customerShareData, setCustomerShareData] = useState<
    CustomerSharingResponse[]
  >([
    {
      shareAgain: false,
      lastUpdatedBy: 0,
      lastUpdateDate: "",
      subDomain: "",
      isDelete: false,
      hasCreditorPortal: "",
      accountNumber: "",
      creationDate: "",
      schemaName: "",
      styleClass: "",
      isSharing: "",
      isEditable: false,
      createdBy: 0,
      lenderName: "",
      customerId: 0,
      revokeBtn: false,
      creditor: "",
      id: 0,
      shareNow: "",
      lenderOrgId: 0,
    },
  ]);

  const [
    hasVulnerabilityRegistrationService,
    setHasVulnerabilityRegistrationService,
  ] = useState(false);

  const [isNationalDebtlineMWN, setIsNationalDebtlineMWN] = useState(false);
  const [isOfferMoneyWellness, setIsOfferMoneyWellness] = useState(false);
  const [showYesEnergy, setShowYesEnergy] = useState(false);
  const [showYWCTMessage, setShowYWCTMessage] = useState(false);
  const [showMLBCard, setShowMLBCard] = useState(false);
  const [showIncomeMaxCard, setShowIncomeMaxCard] = useState(false);
  const [isCadentEligible, setIsCadentEligible] = useState(false);

  const [isLoadedWater, setIsLoadedWater] = useState(false);
  const [isLoadedPcode, setIsLoadedPcode] = useState(false);
  const [disableContinue, setDisableContinue] = useState(false);

  const [waterSupportEntitlement, setWaterSupportEntitlement] = useState("");
  const [waterSureEntitlement, setwaterSureEntitlement] = useState("");
  const [waterCompany, setWaterCompany] = useState("");
  const [extraMonthlyBenefitsAmount, setExtraMonthlyBenefitsAmount] =
    useState(null);

  const [additionalSupportCompanies, setAdditionalSupportCompanies] = useState<
    AdditionalSupportEntry[]
  >([
    {
      companyName: "",
      customerId: 0,
    },
  ]);

  const [selectedMlb, setSelectedMlb] = useState("U");
  const [selectedIm, setSelectedIm] = useState("U");
  const [selectedVrs, setSelectedVrs] = useState("U");
  const [selectedSls, setSelectedSls] = useState("U");
  const [selectedNdl, setSelectedNdl] = useState("U");
  const [selectedPp, setSelectedPp] = useState("U");
  const [selectedCg, setSelectedCg] = useState("U");
  const [selectedYe, setSelectedYe] = useState("U");
  const [selectedYwct, setSelectedYwct] = useState("U");
  const [selectedH4u, setSelectedH4u] = useState("U");
  const [showHope4UCard, setShowHope4UCard] = useState(false);

  const [openDetailsKey, setOpenDetailsKey] = useState<string | null>(null);
  const [shouldShowPayPlan, setShouldShowPayPlan] = useState(false);
  const [debts, setDebts] = useState<[]>([]);
  const [loadedDebts, setLoadedDebts] = useState<boolean>(false);
  const [isAtBottom, setIsAtBottom] = useState<boolean>(false);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [dependantDobData, setDependantDobData] = useState<
    DependentDobRecord[]
  >([]);
  const [dependantDobDataLoaded, setDependantDobDataLoaded] =
    useState<boolean>(false);

  const checkScrollBottom = () => {
    const el = contentRef.current;
    if (el) {
      const scrollPosition = el.scrollTop + el.clientHeight;
      const atBottom = scrollPosition >= el.scrollHeight - 1;
      setIsAtBottom(atBottom);
    }
  };

  const hasFetchedCustomerShareData = useRef(false);

  // GET customer share data — first API call on Additional Support landing
  useEffect(() => {
    if (hasFetchedCustomerShareData.current) return;
    hasFetchedCustomerShareData.current = true;

    const rawCustomerData = localStorage.getItem("customerData");
    if (!rawCustomerData) {
      return;
    }

    let customerData: Record<string, unknown> | null = null;
    try {
      customerData = JSON.parse(rawCustomerData) as Record<string, unknown>;
    } catch {
      return;
    }

    const shareCustomerId =
      (typeof customerData.customerId === "number" ||
      typeof customerData.customerId === "string"
        ? customerData.customerId
        : undefined) ??
      (typeof customerData.id === "number" || typeof customerData.id === "string"
        ? customerData.id
        : undefined);

    if (shareCustomerId === undefined || shareCustomerId === "") {
      return;
    }

    async function getCustomerShareData() {
      const data: any = await SharePageService.getCustomerSharing(
        shareCustomerId as string | number,
      );

      setCustomerShareData(data);

      // Set initial toggles for all cards
      const useStateMap: Record<string, (...args: any[]) => void> = {
        mlb: setSelectedMlb,
        im: setSelectedIm,
        vrs: setSelectedVrs,
        sls: setSelectedSls,
        ndl: setSelectedNdl,
        pp: setSelectedPp,
        cg: setSelectedCg,
        ye: setSelectedYe,
        ywct: setSelectedYwct,
        h4u: setSelectedH4u,
      };

      const reverseLenderNameMap: Record<string, string> = {
        "Make Life Better": "mlb",
        "Income Max": "im",
        "Vulnerability Registration Service": "vrs",
        "Stop Loan Sharks": "sls",
        "National Debtline": "ndl",
        PayPlan: "pp",
        "Cadent Gas": "cg",
        "YES Energy Solutions": "ye",
        "Yorkshire Water Community Trust": "ywct",
        Hope4u: "h4u",
      };

      for (const entry of data) {
        if (entry.isDeleted == "Y" || entry.isSharing == "N") {
          continue;
        }

        const company = entry.lenderName;
        if (reverseLenderNameMap.hasOwnProperty(company)) {
          const key = reverseLenderNameMap[company] as string;
          useStateMap[key]?.(entry.shareNow);
        }
      }
    }

    void getCustomerShareData();
  }, []);

  // GET dependent DOB data
  useEffect(() => {
    async function getDependantData() {
      const customerData = JSON.parse(
        localStorage.getItem("customerData") as string,
      );

      const dependentsResponse = await DependentsService.getDependents(
        customerData.customerId as number,
      );

      if (dependentsResponse.success && dependentsResponse.data) {
        setDependantDobData(dependentsResponse.data);
        setDependantDobDataLoaded(true);
      }
    }

    getDependantData();
  }, []);

  useEffect(() => {
    let timer: string | number | NodeJS.Timeout | undefined;
    if (visible && count > 0) {
      timer = setInterval(() => {
        setCount((prev) => prev - 1);
      }, 1000);
    } else if (count === 0) {
      setVisible(false);
      setDisableMwgBtns(false);
    }

    return () => clearInterval(timer);
  }, [count, visible]);

  useEffect(() => {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    if (customerData.mobileNumber) {
      setHasMobileNumber(true);
    }
  }, []);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    el.addEventListener("scroll", checkScrollBottom);

    setTimeout(checkScrollBottom, 0);

    return () => {
      el.removeEventListener("scroll", checkScrollBottom);
    };
  }, []);

  function buildCompanyData(companyName: string) {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    // Look up the company in the master lender list (parent and child lenders) to get its sharing details
    const lenderRaw = findMasterLender(ieMstrLenderData, companyName);

    return {
      lender: lenderRaw?.lender ?? companyName,
      schemaName: lenderRaw?.schemaName,
      subDomain: lenderRaw?.subDomain,
      lenderOrgId: lenderRaw?.orgId ?? undefined, // dropped from the JSON when the lender has no orgId
      accountNumber: customerData.emailAddress as string,
    };
  }

  const lenderNameMap: Record<string, string> = {
    mlb: "Make Life Better",
    im: "Income Max",
    vrs: "Vulnerability Registration Service",
    sls: "Stop Loan Sharks",
    ndl: "National Debtline",
    pp: "PayPlan",
    cg: "Cadent Gas",
    ye: "YES Energy Solutions",
    ywct: "Yorkshire Water Community Trust",
    h4u: "Hope4u",
  };

  const handleShareCompany = async (choice: string, company: string) => {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    const useStateMap: Record<string, (...args: any[]) => void> = {
      mlb: setSelectedMlb,
      im: setSelectedIm,
      vrs: setSelectedVrs,
      sls: setSelectedSls,
      ndl: setSelectedNdl,
      pp: setSelectedPp,
      cg: setSelectedCg,
      ye: setSelectedYe,
      ywct: setSelectedYwct,
      h4u: setSelectedH4u,
    };

    const companyName: string = lenderNameMap[company] || "";

    useStateMap[company]?.(choice);

    const shareEntriesForCompany = customerShareData.filter(
      (item) => item.lenderName === companyName && item.isDeleted != "Y",
    );

    const isDisabled = companies.filter(
      (c) =>
        c.disableShare &&
        c.raw?.isDeleted != "Y" &&
        c.raw?.isSharing != "N" &&
        c.raw?.lenderName == companyName,
    );

    if (isDisabled.length > 0) {
      return;
    }

    // We need to create an entry
    if (shareEntriesForCompany.length == 0) {
      if (choice == "Y") {
        const sharingCompany = buildCompanyData(companyName);

        // Create new share entry
        try {
          const payload = {
            accountNumber: sharingCompany.accountNumber,
            customerId: customerData.customerId,
            isSharing: "Y",
            lenderName: sharingCompany.lender,
            schemaName: sharingCompany.schemaName,
            subDomain: sharingCompany.subDomain,
            lenderOrgId: sharingCompany.lenderOrgId,
            shareNow: choice,
            source: "additional-support",
          };

          const response = await httpPost<CustomerSharingResponse>(
            sharePageEndpoints.createCustomerSharing(),
            payload,
          );

          setCompanyShareLastUpdateDate(
            new Date(response.data.lastUpdateDate).getTime(),
          );

          // To prevent duplicate requests
          setCustomerShareData([...customerShareData, response.data]);

          const docsResp = await getCompanyDocs(
            String(response.data.id),
            response.data.lenderName,
            response.data.accountNumber,
          );
          const docsArray: SharedDocResponse[] = Array.isArray(docsResp)
            ? (docsResp as SharedDocResponse[])
            : (docsResp?.data ?? []);
          const documents: Document[] = docsArray.map(
            (doc: SharedDocResponse) => {
              return {
                id: String(doc.id),
                name: doc.documentName || doc.fileName,
                size: doc.documentSize,
                type: "wage-slip",
                uploadedDate: formatDate(doc.lastUpdateDate),
                raw: doc,
              };
            },
          );

          const obj = {
            declarationData: undefined,
            id: parseInt(response.data.id),
            name: sharingCompany.lender,
            reference: sharingCompany.accountNumber,
            type: "all",
            address: "",
            documents,
            selectedDocuments: [],
            lastShared: "",
            raw: response.data,
            isFp: false,
          };

          if (sharingCompany.lender == "National Debtline") {
            obj.ndlData = {
              debtAdviceContactPreferenceNdl: "Y",
              emailMeNdl: false,
              textMeNdl: true,
            };
          }

          setCompanies((prev) => [...prev, obj]);
        } catch (error) {
          femaCapture("creating_share_company_data_error", error, {
            category: "api",
            severity: "error",
            skipSanitization: false,
            user: {},
          });
          throw error;
        }
      }

      return;
    }

    // There is already an entry, update existing ones
    for (const entry of shareEntriesForCompany) {
      const companyEntry = companies.find((c) => c.id === entry.id);
      const disableShare = Boolean(companyEntry?.disableShare);

      const payload = {
        ...entry,
        shareNow: disableShare ? "N" : choice,
        isSharing: "Y",
        source:
          entry.source === "customer_profile"
            ? "customer_profile"
            : "additional-support",
      };

      // Ensure "editable" is not sent to the API as the backend model does not support it
      const sanitizedPayload = { ...payload };
      delete (sanitizedPayload as any).editable;

      const { data } = await httpPut<CustomerSharingResponse>(
        sharePageEndpoints.createCustomerSharing(),
        sanitizedPayload,
      );

      const newShareData = customerShareData.map((entry) => {
        if (entry.id == data.id) {
          entry = data;
        }

        return entry;
      });

      setCustomerShareData(newShareData);

      setCompanies((prev) => {
        return prev.map((c) => {
          return c.id === data.id
            ? {
                ...c,
                raw: data,
              }
            : c;
        });
      });
    }
  };

  async function sendWaterCompany(): Promise<undefined | string> {
    try {
      // Resolve the canonical master-list name using a partial starts-with
      // match so that an Inbest name like "Severn Trent" correctly maps to
      // the master list entry "Severn Trent Water Ltd".
      const masterEntry = findFreepostByPartialName(
        freepostCreditorNamesList,
        waterCompany,
      );
      const canonicalName = masterEntry?.creditor ?? waterCompany;

      // Skip if already on the share list (match on canonical name)
      let skip = false;
      for (const c of companies) {
        if (c.name == canonicalName && c.raw?.isDeleted != "Y") {
          skip = true;
          break;
        }
      }

      // Also check against the digital lenders list using partial match
      const isDigital = creditorNamesList.some((e: any) =>
        isPartialNameMatch(e.creditor, waterCompany)
      );

      if (skip) {
        return;
      }

      if (isDigital) {
        return "is-digital";
      }
      const customerData = JSON.parse(
        localStorage.getItem("customerData") as string,
      );

      if (waterSupportEntitlement != "Y" && waterSureEntitlement != "Y") {
        return;
      }

      if (!masterEntry) {
        console.debug(
          `[ASP] Water company "${waterCompany}" not found in freepost master list (exact or partial match).`,
        );
        return;
      }

      let coveringNotes = `I am in financial difficulty and because of this I have used IE Hub to review my budget. I have enclosed a copy for your record. You will be able to see from this information that I'm unable to make my full contractual payments to you.`;

      if (customerData?.mobileNumber) {
        coveringNotes = `I am in financial difficulty and because of this I have used IE Hub to review my budget. I have enclosed a copy for your record. You will be able to see from this information that I'm unable to make my full contractual payments to you. Please call me on ${customerData.mobileNumber} so we can discuss my options.`;
      }

      // Use the canonical master-list name so the record is stored under the
      // correct name and is recognised by the Share List freepost check.
      const payload = {
        companyName: canonicalName,
        referenceNumber: customerData.emailAddress,
        address: masterEntry?.creditorAddress,
        notes: coveringNotes,
        isTcAccepted: "Y",
        customerId: customerData.customerId,
        userId: customerData.userId,
        isApiCall: "Y",
        isSharing: "Y",
        shareNow: "N",
        source: "additional-support",
      };

      const response = await httpPost<CustomerSharingResponse>(
        sharePageEndpoints.createFreepostSharing(),
        payload,
      );

      setCompanies((prev) => [
        ...prev,
        {
          declarationData: undefined,
          id: String(payload.referenceNumber),
          name: canonicalName,
          reference: String(payload.referenceNumber),
          type: "all",
          address: masterEntry?.creditorAddress ?? "",
          documents: [],
          selectedDocuments: [],
          lastShared: "",
          raw: response.data,
          isFp: true,
        },
      ]);

      setFreePostLastUpdateDate((prev) => Math.max(prev, Date.now()));
    } catch (e) {
      console.error(e);
    }
  }

  async function sendSupportCompanies() {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    const companiesList: string[] = [];
    let shareNowMap: Record<string, string> = {
      "Electricity North West": "N",
      "Yorkshire Water Community Trust": "N",
      "United Utilities": "N",
    };

    const over65 = isOver65(customerData.debtor1DateOfBirthStr);
    const dependantOver5 =
      (await hasDependantOver5()) ||
      (customerData.dependentChildrenAged16Plus &&
        customerData.dependentChildrenAged16Plus != 0);
    const dependantUnder5 = await hasDependantUnder5();

    const hasVulnerability =
      customerData.field3TxtFld && customerData.field3TxtFld !== "N";

    const enwCondition =
      over65 || (!dependantOver5 && dependantUnder5) || hasVulnerability;

    if (enwCondition) {
      shareNowMap["Electricity North West"] = "Y";
    }

    const digitalStatus = await sendWaterCompany();

    // electric company
    // UKPN hidden: original code kept below, uncomment to restore
    // const electricCompanyName = showENWMessage
    //   ? "Electricity North West"
    //   : showUKPNMessage
    //     ? "UK Power Networks"
    //     : "";
    const electricCompanyName = showENWMessage
      ? "Electricity North West"
      : "";

    if (electricCompanyName) {
      companiesList.push(electricCompanyName);
    }

    if (isCitizensAdviceMidLincolnshire) {
      companiesList.push("Citizens Advice Mid Lincolnshire");
    }

    if (showCitizensAdvicePlymouth) {
      companiesList.push("Citizens Advice Plymouth");
    }

    if (showWestViewAdviceAndResourceCentre) {
      companiesList.push("West View Advice and Resource Centre");
    }

    if (showYesEnergy) {
      companiesList.push("YES Energy Solutions");
    }

    if (showYWCTMessage) {
      companiesList.push("Yorkshire Water Community Trust");
    }

    if (
      digitalStatus == "is-digital" &&
      (waterSupportEntitlement == "Y" || waterSureEntitlement == "Y")
    ) {
      companiesList.push(waterCompany);
    }

    // postcode clients with addToAspPage === "Y"
    const clientsToAddToTop = postcodeClients
      .filter((i) => i.addToAspPage?.toUpperCase() === "Y" && i.clientName)
      .map((i) => i.clientName);

    // additional support companies with addToAspPage === "Y"
    const aspCompaniesToAddToTop = additionalSupportCompanies
      .filter((i) => (i as any).addToAspPage?.toUpperCase() === "Y" && i.companyName)
      .map((i) => i.companyName);

    const allClientsToAddToTop = Array.from(
      new Set([...clientsToAddToTop, ...aspCompaniesToAddToTop])
    );

    for (const clientName of allClientsToAddToTop) {
      if (!companiesList.includes(clientName)) {
        companiesList.push(clientName);
      }
    }

    const resolvedCustomerId: string | number | null = await getCustomerId();

    // Build the full set of LENDER names already on the share list (still sharing).
    // Compare by lender (e.g. "CAB"), not description
    // ("Caerphilly and Blaenau Gwent Citizens Advice Bureau").
    console.log("companies", companies);
    console.log("customerShareData", customerShareData);
    const existingShareListLenders = new Set<string>(
      [
        ...companies
          .filter((c) => c.raw?.isSharing != "N" && c.raw?.isDeleted != "Y")
          .map((c) => c.raw?.lenderName || c.name),
        ...customerShareData
          .filter((e) => e.isSharing != "N" && e.isDeleted != "Y")
          .map((e) => e.lenderName),
      ].filter((name): name is string => Boolean(name)),
    );

    for (const company of companiesList) {
      // Resolve description → lender before comparing (e.g. full CAB name → "CAB")
      const sharingCompany = buildCompanyData(company);
      const lenderKey = sharingCompany.lender;
      const skip = existingShareListLenders.has(lenderKey);
      console.log("skip", company, lenderKey, skip);

      if (skip) {
        if (isCoreAspCompany(lenderKey) || isCoreAspCompany(company)) {
          const existingEntry = customerShareData.find(
            (e) =>
              (e.lenderName === lenderKey || e.lenderName === company) &&
              e.isDeleted !== "Y",
          );
          if (
            existingEntry &&
            existingEntry.source !== "customer_profile" &&
            existingEntry.source !== "additional-support"
          ) {
            const payload = {
              ...existingEntry,
              source: "additional-support",
            };
            const response = await httpPut<CustomerSharingResponse>(
              sharePageEndpoints.createCustomerSharing(),
              payload,
            );

            setCompanies((prev) =>
              prev.map((c) =>
                c.id == response.data.id ? { ...c, raw: response.data } : c,
              ),
            );
          }
        }
        continue;
      }

      try {
        const payload = {
          accountNumber: sharingCompany.accountNumber,
          customerId: resolvedCustomerId,
          isSharing: "Y",
          lenderName: sharingCompany.lender,
          schemaName: sharingCompany.schemaName,
          subDomain: sharingCompany.subDomain,
          lenderOrgId: sharingCompany.lenderOrgId,
          shareNow: shareNowMap[company] || shareNowMap[lenderKey] || "Y",
          source: "additional-support",
        };

        const response = await httpPost<CustomerSharingResponse>(
          sharePageEndpoints.createCustomerSharing(),
          payload,
        );

        // Mark lender as present so later companies in this loop don't duplicate-POST
        existingShareListLenders.add(lenderKey);

        setCompanyShareLastUpdateDate(
          new Date(response.data.lastUpdateDate).getTime(),
        );

        const docsResp = await getCompanyDocs(
          String(response.data.id),
          response.data.lenderName,
          response.data.accountNumber,
        );
        const docsArray: SharedDocResponse[] = Array.isArray(docsResp)
          ? (docsResp as SharedDocResponse[])
          : (docsResp?.data ?? []);
        const documents: Document[] = docsArray.map(
          (doc: SharedDocResponse) => {
            return {
              id: String(doc.id),
              name: doc.documentName || doc.fileName,
              size: doc.documentSize,
              type: "wage-slip",
              uploadedDate: formatDate(doc.lastUpdateDate),
              raw: doc,
            };
          },
        );

        setCompanies((prev) => [
          ...prev,
          {
            declarationData: undefined,
            id: String(response.data.id),
            name: sharingCompany.lender,
            reference: sharingCompany.accountNumber,
            type: "all",
            address: "",
            documents,
            selectedDocuments: [],
            lastShared: "",
            raw: response.data,
            isFp: false,
            declarationStatus: "not-completed",
          },
        ]);
      } catch (error) {
        femaCapture("creating_share_company_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
      }
    }
  }

  async function handleSave() {
    setDisableContinue(true);

    const aspShowMap: Record<string, boolean> = {
      mlb: showMLBCard,
      im: showIncomeMaxCard,
      vrs: hasVulnerabilityRegistrationService,
      sls: isSLSEligible,
      ndl: isNationalDebtlineMWN,
      pp: shouldShowPayPlan,
      cg: isCadentEligible,
      ye: showYesEnergy,
      ywct: showYWCTMessage,
      h4u: showHope4UCard,
    };

    // Update existing ASP companies that are already selected as Y
    for (const [key, isShown] of Object.entries(aspShowMap)) {
      if (isShown) {
        const lenderName = lenderNameMap[key];
        const existingEntries = customerShareData.filter(
          (e) => e.lenderName === lenderName && e.isDeleted !== "Y",
        );
        for (const entry of existingEntries) {
          if (
            entry.shareNow === "Y" &&
            entry.source !== "customer_profile" &&
            entry.source !== "additional-support"
          ) {
            const payload = {
              ...entry,
              source: "additional-support",
            };
            const response = await httpPut<CustomerSharingResponse>(
              sharePageEndpoints.createCustomerSharing(),
              payload,
            );

            setCompanies((prev) =>
              prev.map((c) =>
                c.id == response.data.id ? { ...c, raw: response.data } : c,
              ),
            );
          }
        }
      }
    }

    const eligibleConditions: string[] = [];
    if (showMLBCard) eligibleConditions.push("Make Life Better");
    if (showIncomeMaxCard) eligibleConditions.push("Income Max");
    if (hasVulnerabilityRegistrationService)
      eligibleConditions.push("Vulnerability Registration Service");
    if (isSLSEligible)
      eligibleConditions.push("Illegal Money Lending Team (Stop Loan Sharks)");
    if (isNationalDebtlineMWN) eligibleConditions.push("National Debtline");
    if (shouldShowPayPlan) eligibleConditions.push("PayPlan");
    if (isCadentEligible) eligibleConditions.push("Cadent Gas");
    if (showYesEnergy) eligibleConditions.push("YES Energy Solutions");
    if (showYWCTMessage)
      eligibleConditions.push("Yorkshire Water Community Trust");
    if (showHope4UCard) eligibleConditions.push("Hope4u");

    onEligibleConditions?.(eligibleConditions);
    
    // selected support partners
    for (const company of selectedSupportPartners) {
      const sharingCompany = buildCompanyData(company);
      const resolvedCustomerId: string | number | null = await getCustomerId();

      const existingEntry = customerShareData.find(
        (e) => e.lenderName === sharingCompany.lender && e.isDeleted !== "Y",
      );

      if (existingEntry) {
        if (
          isCoreAspCompany(sharingCompany.lender) &&
          existingEntry.source !== "customer_profile" &&
          existingEntry.source !== "additional-support"
        ) {
          const payload = {
            ...existingEntry,
            source: "additional-support",
          };
          const response = await httpPut<CustomerSharingResponse>(
            sharePageEndpoints.createCustomerSharing(),
            payload,
          );

          setCompanies((prev) =>
            prev.map((c) =>
              c.id == response.data.id ? { ...c, raw: response.data } : c,
            ),
          );
        }
        continue;
      }

      try {
        const payload = {
          accountNumber: sharingCompany.accountNumber,
          customerId: resolvedCustomerId,
          isSharing: "Y",
          lenderName: sharingCompany.lender,
          schemaName: sharingCompany.schemaName,
          subDomain: sharingCompany.subDomain,
          lenderOrgId: sharingCompany.lenderOrgId,
          shareNow: "Y",
          source: "additional-support",
        };

        const response = await httpPost<CustomerSharingResponse>(
          sharePageEndpoints.createCustomerSharing(),
          payload,
        );

        setCompanyShareLastUpdateDate(
          new Date(response.data.lastUpdateDate).getTime(),
        );

        const docsResp = await getCompanyDocs(
          String(response.data.id),
          response.data.lenderName,
          response.data.accountNumber,
        );
        const docsArray: SharedDocResponse[] = Array.isArray(docsResp)
          ? (docsResp as SharedDocResponse[])
          : (docsResp?.data ?? []);
        const documents: Document[] = docsArray.map(
          (doc: SharedDocResponse) => {
            return {
              id: String(doc.id),
              name: doc.documentName || doc.fileName,
              size: doc.documentSize,
              type: "wage-slip",
              uploadedDate: formatDate(doc.lastUpdateDate),
              raw: doc,
            };
          },
        );

        setCompanies((prev) => [
          ...prev,
          {
            declarationData: undefined,
            id: String(response.data.id),
            name: sharingCompany.lender,
            reference: sharingCompany.accountNumber,
            type: "all",
            address: "",
            documents,
            selectedDocuments: [],
            lastShared: "",
            raw: response.data,
            isFp: false,
            declarationStatus: "not-completed",
          },
        ]);
      } catch (error) {
        setDisableContinue(false);
        femaCapture("creating_share_company_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    // for support company cards
    await sendSupportCompanies();

    onClose();
  }

  const hasFetchedClientsByPcode = useRef(false);

  // GET clients by postcode
  useEffect(() => {
    if (!loadedDebts || !dependantDobDataLoaded || !loadedBenefitsAmount) {
      return;
    }

    if (hasFetchedClientsByPcode.current) return;
    hasFetchedClientsByPcode.current = true;

    const rawCustomerData = localStorage.getItem("customerData");
    if (!rawCustomerData) {
      return;
    }

    let customerData: Record<string, unknown> | null = null;
    try {
      customerData = JSON.parse(rawCustomerData) as Record<string, unknown>;
    } catch {
      customerData = null;
    }

    const rawPostcode =
      (customerData?.postCode as string | undefined) ??
      (customerData?.postcode as string | undefined) ??
      "";

    const postcodeTrimmed = rawPostcode
      .toString()
      .toLowerCase()
      .replace(/\s+/g, "");

    const postcodeCustomerId =
      typeof customerData?.customerId === "number" ||
      typeof customerData?.customerId === "string"
        ? customerData.customerId
        : undefined;

    async function getClientByPostcode(): Promise<ServiceResponse<any[]>> {
      try {
        const response = await httpGet<ApiResponse>(
          onboardingEndpoints.getPostcodesByPostCode(
            postcodeTrimmed,
            postcodeCustomerId,
          ),
        );
        return response.data;
      } catch (error) {
        femaCapture("fetching_postcodes_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    getClientByPostcode().then(async (res: any) => {
      if (!res || !Array.isArray(res)) return;

      setPostcodeClients(res);

      const enw = res.some((i) => i.clientName === "ENW");
      const ukpn = res.some((i) => i.clientName === "UKPN");
      const westView = res.some(
        (i) => i.clientName === "West View Advice and Resource Centre",
      );
      const citizens = res.some(
        (i) => i.clientName === "Citizens Advice Mid Lincolnshire",
      );
      const uuWellness = res.some((i) => i.clientName === "United Utilities");
      const showCitizensAdvicePlymouth =
        rawPostcode.toString().toLowerCase().startsWith("pl") &&
        debts.length > 0;

      setShowENWMessage(enw);
      setShowUKPNMessage(ukpn);
      setShowWestViewAdviceAndResourceCentre(westView);
      setIsCitizensAdviceMidLincolnshire(citizens);
      setIsUUWellnessRelatedPostCode(uuWellness);
      setShowCitizensAdvicePlymouth(showCitizensAdvicePlymouth);
      setIsLoadedPcode(true);
    });
  }, [
    debts,
    loadedDebts,
    dependantDobData,
    dependantDobDataLoaded,
    loadedBenefitsAmount,
  ]);

  useEffect(() => {
    let isMounted = true;

    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    const fetchInbestData = async () => {
      try {
        const resp = await BenefitsService.startCheck(customerData.customerId, {
          skipErrorCapture: true,
        });

        if (!isMounted || !resp?.success || !resp.data) return;

        const {
          extraMonthlyBenefitsAmount,
          waterSupportEntitlement,
          waterSureEntitlement,
          providerName,
        } = resp.data;

        localStorage.setItem(
          "BENEFITS_AMOUNT",
          String(extraMonthlyBenefitsAmount ?? 0),
        );
        setLoadedBenefitsAmount(true);

        setWaterSupportEntitlement(waterSupportEntitlement as string);
        setwaterSureEntitlement(waterSureEntitlement as string);
        setWaterCompany(providerName as string);
        setExtraMonthlyBenefitsAmount(extraMonthlyBenefitsAmount);
        setIsLoadedWater(true);
      } catch (e) {
        console.warn("Failed to fetch inbest data:", e);
        setLoadedBenefitsAmount(true);
        setIsLoadedWater(true);

        femaCapture("fetching_inbest_data_error", e, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
      }
    };

    fetchInbestData();

    return () => {
      isMounted = false;
    };
  }, []);

  // Reconcile wellness related postcode flag from both Postcode API and Inbest API
  useEffect(() => {
    if (!isLoadedPcode) return; // Wait for postcode API to finish

    const postcodeWellness = postcodeClients.some((i) =>
      ["Bristol Water", "Wessex Water"].includes(i.clientName)
    );

    if (postcodeWellness) {
      setIsWellnessRelatedPostCode(true);
    } else {
      // postcode api does not return bristol or wessex water, check Inbest response
      if (isLoadedWater) {
        const inbestWellness = ["Bristol Water", "Wessex Water"].includes(waterCompany);
        setIsWellnessRelatedPostCode(inbestWellness);
      }
    }
  }, [postcodeClients, waterCompany, isLoadedPcode, isLoadedWater]);

  const hasFetchedDebts = useRef(false);

  // GET debts
  useEffect(() => {
    if (hasFetchedDebts.current) return;
    hasFetchedDebts.current = true;

    async function getDebts() {
      const resolvedCustomerId: string | number | null = await getCustomerId();
      const debts = await OnboardingService.getDebts(
        Number(resolvedCustomerId),
        {
          skipErrorCapture: true,
        },
      );
      return debts;
    }

    getDebts().then((debts) => {
      setDebts(debts);
      setLoadedDebts(true);
    });
  }, []);

  // GET Creditor List Names
  useEffect(() => {
    async function getCreditorNames(): Promise<DigitalLender[]> {
      return digitalLenders;
    }

    getCreditorNames().then((data: any) => {
      setCreditorNamesList(data);
    });
  }, [digitalLenders]);

  async function sendDebtAdvice(notificationSource: string) {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    let companyName = customerData.field5TxtFld;

    if (
      companyName === "I have not been referred by a company" &&
      isWellnessRelatedPostCode
    ) {
      companyName = "Bristol Water";
    }

    const payload: NotifyCustomerDebtAdvice = {
      companyName,
      userId: customerData.userId,
      isUnitedUtilitiesCompany: "N",
      mobileNumber: customerData.mobileNumber,
      notificationSource,
    };

    const resp = await httpPost<NotifyCustomerDebtAdviceResponse>(
      customerEndpoints.notifyCustomerDebtAdvice(),
      payload,
    );

    const msg = resp.data.notifyInformation;

    showAlert({
      severity: msg.toLowerCase().includes("fail") ? "error" : "success",
      message: msg,
      autoHide: true,
      duration: 2000,
    });

    setDisableMwgBtns(true);
    setCount(30);
    setVisible(true);
  }

  const hasFetchedAdditionalSupportCompanies = useRef(false);

  const hasDependantUnder5 = useCallback(async (): Promise<boolean> => {
    return dependantDobData.some((dep) => {
      const ageYears = calculateAge(
        dep.childDependantDobStr ?? dep.childDependantDob,
      );
      return typeof ageYears === "number" && ageYears < 5;
    });
  }, [dependantDobData]);

  const hasDependantOver5 = useCallback(async (): Promise<boolean> => {
    return dependantDobData.some((dep) => {
      const ageYears = calculateAge(
        dep.childDependantDobStr ?? dep.childDependantDob,
      );
      return typeof ageYears === "number" && ageYears > 5;
    });
  }, [dependantDobData]);

  // GET additional support companies
  useEffect(() => {
    if (!dependantDobDataLoaded || !loadedBenefitsAmount || !isLoadedWater) {
      return;
    }

    if (hasFetchedAdditionalSupportCompanies.current) return;
    hasFetchedAdditionalSupportCompanies.current = true;

    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    async function getAdditionalSupportCompanies(): Promise<
      ServiceResponse<any[]>
    > {
      try {
        const queryParams = [
          { param: "customerId", value: customerData.customerId },
          {
            param: "extraBenefitAmount",
            value:
              extraMonthlyBenefitsAmount ||
              localStorage.getItem("BENEFITS_AMOUNT") ||
              "0",
          },

          {
            param: "childrenAgeUnderFive",
            value: (await hasDependantUnder5()) ? "Y" : "N",
          },
          {
            param: "customerHasPensionAge",
            value: isOver65(customerData.debtor1DateOfBirthStr) ? "Y" : "N",
          },
          { param: "jointAccount", value: customerData.jointAccount || "N" },
          {
            param: "dependentChildrenUnder16",
            value: customerData.dependentChildrenUnder16 || 0,
          },
          {
            param: "dependentchildrenaged16plus",
            value: customerData.dependentChildrenAged16Plus || 0,
          },
          { param: "postCode", value: customerData.postcodeTrimmed },
          {
            param: "vulnerabilityConsent",
            value:
              (String(
                customerData.field2TxtFld || customerData.field2Txtfld || "",
              ).toUpperCase() === "Y" ||
                String(
                  customerData.field2TxtFld || customerData.field2Txtfld || "",
                ).toUpperCase() === "YES") &&
              coerceConsentToHuman(customerData.field4TxtFld) === "Yes"
                ? "Yes"
                : "No",
          },
          {
            param: "referredCompany",
            value: customerData.field5TxtFld,
          },
        ];

        const response = await httpGet<ApiResponse>(
          // isOver65
          customerEndpoints.showAdditionalSupportCompanies() +
            buildQueryString(queryParams),
        );

        return response.data;
      } catch (error) {
        femaCapture("fetching_additional_support_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    getAdditionalSupportCompanies().then((res: any) => {
      setAdditionalSupportCompanies(res);
    });
  }, [
    dependantDobData,
    dependantDobDataLoaded,
    loadedBenefitsAmount,
    isLoadedWater,
  ]);

  const hasFetchedIeMstrLenderSupporterNotShared = useRef(false);

  // GET getIeMstrLenderSupporterNotShared companies
  useEffect(() => {
    if (hasFetchedIeMstrLenderSupporterNotShared.current) return;
    hasFetchedIeMstrLenderSupporterNotShared.current = true;

    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    async function getIeMstrLenderSupporterNotShared(): Promise<
      ServiceResponse<any[]>
    > {
      try {
        const response = await httpGet<ApiResponse>(
          customerEndpoints.listNotSharedLenders(
            Number(customerData.customerId),
          ),
        );

        return response.data;
      } catch (error) {
        femaCapture("fetching_lender_support_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    getIeMstrLenderSupporterNotShared().then((res: any) => {
      setSupportPartnersNames(
        res.map((item: LenderNotShared) => item.description) || [],
      );
    });
  }, []);

  // Build support card messages
  useEffect(() => {
    const waterSupportQualified =
      (waterSupportEntitlement == "Y" || waterSureEntitlement == "Y") &&
      (waterCompany != undefined || waterCompany != "");

    const recommendationCompanies = waterSupportQualified ? waterCompany : "";

    const messages = {
      showENWMessage: "Electricity North West",
      showUKPNMessage: "UK Power Networks",
      showWestViewAdviceAndResourceCentre:
        "West View Advice and Resource Centre",
      showCitizensAdviceMidLincolnshire: "Citizens Advice Mid Lincolnshire",
      showYWCTMessage: "Yorkshire Water Community Trust",
      showCitizensAdvicePlymouth: "Citizens Advice Plymouth",
    };

    const utilityMessages = [];

    if (showENWMessage) utilityMessages.push(messages.showENWMessage);
    // UKPN hidden
    // if (showUKPNMessage) utilityMessages.push(messages.showUKPNMessage);
    if (showWestViewAdviceAndResourceCentre)
      utilityMessages.push(messages.showWestViewAdviceAndResourceCentre);
    if (isCitizensAdviceMidLincolnshire)
      utilityMessages.push(messages.showCitizensAdviceMidLincolnshire);
    if (showYWCTMessage) utilityMessages.push(messages.showYWCTMessage);
    if (showCitizensAdvicePlymouth)
      utilityMessages.push(messages.showCitizensAdvicePlymouth);

    // Get postcode clients with addToAspPage === "Y"
    const clientsToAddToTop = postcodeClients
      .filter((i) => i.addToAspPage?.toUpperCase() === "Y" && i.clientName)
      .map((i) => i.clientName);

    // Get additional support companies with addToAspPage === "Y"
    const aspCompaniesToAddToTop = additionalSupportCompanies
      .filter((i) => (i as any).addToAspPage?.toUpperCase() === "Y" && i.companyName)
      .map((i) => i.companyName);

    const allClientsToAddToTop = Array.from(
      new Set([...clientsToAddToTop, ...aspCompaniesToAddToTop])
    );

    let baseCompanyList = [...utilityMessages];
    if (recommendationCompanies) {
      baseCompanyList.push(recommendationCompanies);
    }

    const normalizedTopClients = allClientsToAddToTop.map((name) =>
      name.trim().toLowerCase()
    );

    const filteredBaseList = baseCompanyList.filter(
      (company) => !normalizedTopClients.includes(company.trim().toLowerCase())
    );

    const combinedCompanyArr = [...allClientsToAddToTop, ...filteredBaseList];

    setCompanyArr(combinedCompanyArr);
  }, [isLoadedWater, isLoadedPcode, postcodeClients, additionalSupportCompanies]);

  const aspLoaded = useCallback(() => {
    return isLoadedPcode && isLoadedWater && loadedDebts;
  }, [isLoadedPcode, isLoadedWater, loadedDebts]);

  // MLB, Income Max, PayPlan  Condition
  useEffect(() => {
    const showMLBCard = additionalSupportCompanies.some(
      (company) => company.companyName === "Make Life Better" && (company as any).addToAspPage?.toUpperCase() !== "Y",
    );
    const showIncomeMaxCard = additionalSupportCompanies.some(
      (company) => company.companyName === "Income Max" && (company as any).addToAspPage?.toUpperCase() !== "Y",
    );

    // True if PayPlan was ever shared (any share-list row with a shared date, deleted ones included).
    const isPayPlanShared =
      Array.isArray(customerShareData) &&
      customerShareData.some(
        (row) =>
          (row.lenderName || "").trim().toLowerCase() === "payplan" &&
          !!(row.lastSharedDate || row.submitDate),
      );
    // Show the PayPlan card only if the additional-support API returns it and it has never been shared.
    const shouldShowPayPlan =
      !isPayPlanShared &&
      additionalSupportCompanies.some(
        (company) => company.companyName === "PayPlan" && (company as any).addToAspPage?.toUpperCase() !== "Y",
      );

    const isCadentEligible = additionalSupportCompanies.some(
      (company) => company.companyName === "Cadent Gas" && (company as any).addToAspPage?.toUpperCase() !== "Y",
    );

    const isNdlEligible = additionalSupportCompanies.some(
      (company) => company.companyName === "National Debtline" && (company as any).addToAspPage?.toUpperCase() !== "Y",
    );

    const isOfferMoneyWellness = additionalSupportCompanies.some(
      (company) => company.companyName === "Money Wellness" && (company as any).addToAspPage?.toUpperCase() !== "Y",
    );

    const showYesEnergyCard = additionalSupportCompanies.some(
      (company) => company.companyName === "YES ENERGY" && (company as any).addToAspPage?.toUpperCase() !== "Y",
    );

    const showYwctCard = additionalSupportCompanies.some(
      (company) => company.companyName === "Yorkshire Water Community Trust" && (company as any).addToAspPage?.toUpperCase() !== "Y",
    );

    const showHope4U = additionalSupportCompanies.some(
      (company) => company.companyName === "Hope4u" && (company as any).addToAspPage?.toUpperCase() !== "Y",
    );

    const showSlsCard = additionalSupportCompanies.some(
      (company) => company.companyName === "Stop Loan Sharks" && (company as any).addToAspPage?.toUpperCase() !== "Y",
    );

    const showVrsCard = additionalSupportCompanies.some(
      (company) => company.companyName === "Vulnerability Registration Service" && (company as any).addToAspPage?.toUpperCase() !== "Y",
    );

    setShowMLBCard(showMLBCard);
    setShowIncomeMaxCard(showIncomeMaxCard);
    setShouldShowPayPlan(shouldShowPayPlan);
    setIsCadentEligible(isCadentEligible);
    setIsNationalDebtlineMWN(isNdlEligible);
    setIsOfferMoneyWellness(isOfferMoneyWellness);
    setShowYesEnergy(showYesEnergyCard);
    setShowYWCTMessage(showYwctCard);
    setShowHope4UCard(showHope4U);
    setIsSLSEligible(showSlsCard);
    setHasVulnerabilityRegistrationService(showVrsCard);
  }, [debts, additionalSupportCompanies, customerShareData]);

  type RenderSupportCompanyCardProps = {
    title: string;
    titleMeta?: string;
    subtitle: string;
    description: React.ReactNode;
    bulletPoints?: string[];
    selectedValue?: "Y" | "N" | string;
    handleShareCompany?: (value: "Y" | "N", companyKey: string) => void;
    companyKey?: string;
    showYesNo?: boolean;
    description2?: string;
    feeDisclosure?: string;
    ctaText?: string;
    logo?: string;
    detailsKey?: string;
    feeLinkLabel?: string;
  };

  function renderSupportCompanyCard({
    title,
    titleMeta,
    subtitle,
    description,
    bulletPoints = [],
    description2,
    feeDisclosure,
    selectedValue,
    handleShareCompany,
    companyKey,
    showYesNo,
    ctaText,
    logo,
    detailsKey,
    feeLinkLabel,
  }: RenderSupportCompanyCardProps) {
    const onInfoClick =
      detailsKey && SUPPORT_DETAILS[detailsKey]
        ? () => setOpenDetailsKey(detailsKey)
        : undefined;
    const cardKey = (companyKey || title).toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const logoSrc = resolveSupportCompanyLogo(logo);
    return (
      <Box data-testid={`pages-private-dashboard-render-support-company-card-container-action-1-${cardKey}`}>
        {/* Header: logo above title + subtitle on phones, side by side from tablet up; info icon top right */}
        <Box
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 1.5,
            mb: logoSrc || onInfoClick ? 1.5 : 0,
          }}
        >
          {logoSrc && (
            <Box
              component="img"
              src={logoSrc}
              alt={`${titleMeta || title} logo`}
              data-testid={`pages-private-dashboard-render-support-company-card-logo-${cardKey}`}
              sx={{
                width: { xs: 80, sm: 88 },
                aspectRatio: "88 / 48",
                objectFit: "contain",
                border: "1px solid #e5e7eb",
                borderRadius: 2,
                p: 0.5,
                bgcolor: "white",
                flexShrink: 0,
              }}
            />
          )}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            {/* Title */}
            <Typography data-testid={`pages-private-dashboard-render-support-company-card-text-action-1-${cardKey}`} variant="h5" sx={{ fontWeight: 600, mb: 0 }}>
              {title}
            </Typography>

            {/* Subtitle */}
            <Typography data-testid={`pages-private-dashboard-render-support-company-card-text-action-2-${cardKey}`}
              variant={logoSrc ? "body2" : "h6"}
              sx={{ fontWeight: 400, mb: logoSrc ? 0 : 1, color: ThemeColours.subtitleVar2 }}
            >
              {subtitle}
            </Typography>
          </Box>
          {onInfoClick && (
            <IconButton
              aria-label={`More information about ${titleMeta || title}`}
              data-testid={`pages-private-dashboard-render-support-company-card-btn-info-${cardKey}`}
              onClick={onInfoClick}
              sx={{
                color: "#6b7280",
                flexShrink: 0,
                position: { xs: "absolute", sm: "static" },
                top: 0,
                right: 0,
                alignSelf: "flex-start",
              }}
            >
              <InfoOutlinedIcon />
            </IconButton>
          )}
        </Box>

        {/* Description */}
        <Box data-testid={`pages-private-dashboard-render-support-company-card-container-action-2-${cardKey}`} sx={{ mb: 1 }}>
          <Typography data-testid={`pages-private-dashboard-render-support-company-card-text-action-3-${cardKey}`}
            variant="h6"
            sx={{ fontWeight: 400, mb: 1, color: ThemeColours.bodyVar1 }}
          >
            {description}
          </Typography>
        </Box>

        {/* Bullet Points */}
        <Box data-testid={`pages-private-dashboard-render-support-company-card-container-action-3-${cardKey}`} sx={{ mb: 1 }}>
          <Box data-testid={`pages-private-dashboard-render-support-company-card-container-action-4-${cardKey}`} sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {bulletPoints.map((item, index) => (
              <Box data-testid={`pages-private-dashboard-render-support-company-card-container-action-5-${cardKey}-${index}`}
                key={index}
                sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
              >
                <CheckCircleIcon sx={{ color: "green", fontSize: 12, mt: "2px" }} />
                <Typography data-testid={`pages-private-dashboard-render-support-company-card-text-action-4-${cardKey}-${index}`}
                  variant="h6"
                  sx={{
                    fontWeight: 400,
                    mb: 0,
                    color: ThemeColours.bodyVar1,
                  }}
                >
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {description2 && (
          <Box data-testid={`pages-private-dashboard-render-support-company-card-container-action-6-${cardKey}`} sx={{ mb: 0 }}>
            <Typography data-testid={`pages-private-dashboard-render-support-company-card-text-action-5-${cardKey}`}
              variant="h6"
              sx={{ fontWeight: 400, mb: 0, color: ThemeColours.bodyVar1 }}
            >
              {description2}
            </Typography>
          </Box>
        )}

        {feeDisclosure && (
          <Box data-testid={`pages-private-dashboard-render-support-company-card-container-action-fee-${cardKey}`} sx={{ mt: 1, mb: 0 }}>
            <Typography data-testid={`pages-private-dashboard-render-support-company-card-text-fee-disclosure-${cardKey}`}
              variant="h6"
              sx={{ fontWeight: 400, mb: 0, color: ThemeColours.bodyVar1 }}
            >
              {feeDisclosure}
              {feeLinkLabel && onInfoClick && (
                <>
                  {" "}
                  <Box
                    component="button"
                    type="button"
                    onClick={onInfoClick}
                    data-testid={`pages-private-dashboard-render-support-company-card-link-fee-${cardKey}`}
                    sx={{
                      background: "none",
                      border: 0,
                      p: 0,
                      font: "inherit",
                      color: "inherit",
                      textDecoration: "underline",
                      cursor: "pointer",
                    }}
                  >
                    {feeLinkLabel}
                  </Box>
                </>
              )}
            </Typography>
          </Box>
        )}

        {/* Share Section */}
        {showYesNo && (
          <Box data-testid={`pages-private-dashboard-render-support-company-card-container-action-7-${cardKey}`}
            sx={{
              backgroundColor: "#EFF6FF",
              border: "1px solid #BFDBFE",
              borderRadius: 2,
              p: 2,
              mb: 0,
              mt: 1,
              display: "flex",
              gap: 1,
            }}
          >
            <Typography data-testid={`pages-private-dashboard-render-support-company-card-text-action-6-${cardKey}`}
              variant="body1"
              sx={{ color: "#1E40AF", lineHeight: 1.6 }}
            >
              {ctaText
                ? ctaText
                : `Would you like us to share your budget with ${titleMeta || title}?`}
              <Box data-testid={`pages-private-dashboard-render-support-company-card-container-action-8-${cardKey}`} sx={{ display: "flex", gap: 1, mt: 1 }}>
                <Button data-testid={`pages-private-dashboard-render-support-company-card-btn-yes-${cardKey}`}
                  variant="contained"
                  onClick={() => {
                    if (!handleShareCompany || !companyKey) return;
                    handleShareCompany("Y", companyKey);
                  }}
                  sx={{
                    backgroundColor:
                      selectedValue === "Y" ? ThemeColours.azul : "#E5E7EB",
                    color: selectedValue === "Y" ? "white" : "black",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor:
                        selectedValue === "Y" ? ThemeColours.azul : "#D1D5DB",
                      boxShadow: "none",
                    },
                    fontWeight: 400,
                  }}
                >
                  Yes
                </Button>

                <Button data-testid={`pages-private-dashboard-render-support-company-card-btn-no-${cardKey}`}
                  variant="contained"
                  onClick={() => {
                    if (!handleShareCompany || !companyKey) return;
                    handleShareCompany("N", companyKey);
                  }}
                  sx={{
                    backgroundColor:
                      selectedValue === "N" ? ThemeColours.azul : "#E5E7EB",
                    color: selectedValue === "N" ? "white" : "black",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor:
                        selectedValue === "N" ? ThemeColours.azul : "#D1D5DB",
                      boxShadow: "none",
                    },
                    fontWeight: 400,
                  }}
                >
                  No
                </Button>
              </Box>
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  const themeSelectRef = useRef<{ open: () => void; close: () => void } | null>(
    null,
  );

  const handleAutocompleteClick = () => {
    themeSelectRef.current?.open?.();
  };

  const cards = [
    {
      id: "Support Card",
      show: companyArr.length > 0,
      content: (
        <>
          <Typography data-testid="pages-private-dashboard-cards-text-action-1-support-card"
            sx={{
              fontSize: 16,
              fontWeight: 400,
              mb: 1,
            }}
          >
            These companies have been added to your share list to make things
            easier. If you don’t want to share your budget with a company, you
            can remove it on the next screen.
          </Typography>

          {companyArr.map((company, index) => (
            <Box data-testid={`pages-private-dashboard-cards-container-action-1-support-card-${index}`}
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: index < companyArr.length - 1 ? 2 : 0,
              }}
            >
              <DotIcon sx={{ fontSize: 8, color: "#00243e" }} />
              <Typography data-testid={`pages-private-dashboard-cards-text-action-2-support-card-${index}`}
                sx={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: "#00243e",
                }}
              >
                {findMasterLender(ieMstrLenderData, company)?.description?.trim() || company}
              </Typography>
            </Box>
          ))}
        </>
      ),
    },
    // PayPlan is the first company offer, right after the "added to your share list" notice (PB-329).
    {
      title: "PayPlan",
      show: shouldShowPayPlan,
      content: renderSupportCompanyCard({
        logo: "/assets/payplan-logo.svg",
        title: "Free debt advice from PayPlan",
        subtitle: "Support from PayPlan",
        description: parseTextWithLinks(
          "It looks like you could benefit from PayPlan's **free debt advice**. They help thousands of people every week to repay their debts in a way that works for them",
        ),
        bulletPoints: [
          "Take the first step to getting back in control of your finances",
          "Share your budget with PayPlan, so you don't need to repeat this step",
          "Receive instant support tailored to your needs",
        ],
        selectedValue: selectedPp,
        handleShareCompany,
        companyKey: "pp",
        showYesNo: true,
        ctaText: "Would you like us to share your budget with PayPlan so you can receive debt advice?",
      }),
    },
    {
      id: "HOPE4U",
      show: showHope4UCard,
      content: renderSupportCompanyCard({
        logo: "Hope4U.svg",
        title: "Free help to increase your household income",
        titleMeta: "Hope4U",
        subtitle: "You may be missing out on money or support you're entitled to.",
        description: "Hope for you provides free support.",
        bulletPoints: [
          "Energy advice to reduce bills and improve energy efficiency and awareness of Low Carbon Technology",
          "Check your income to see if you are entitled to benefits",
          "Sign you up to the PSR to ensure you are kept safe during power cuts",
        ],
        detailsKey: "HOPE4U",
        selectedValue: selectedH4u,
        handleShareCompany,
        companyKey: "h4u",
        showYesNo: true,
        ctaText:
          "Would you like us to share your budget with Hope4U so they can contact you about this free service?",
      }),
    },
    {
      id: "NDL",
      show: isNationalDebtlineMWN,
      content: renderSupportCompanyCard({
        title: "National Debtline",
        subtitle: "Support from National Debtline",
        description:
          "National Debtline provides free and independent debt advice to help you make a clear plan to manage what you owe.",
        bulletPoints: [
          "Free independent debt advice",
          "Clear steps to manage debts",
          "Practical guidance that works",
        ],
        description2:
          "With your consent we’ll share your budget with National Debtline so they can offer you support.",
        selectedValue: selectedNdl,
        handleShareCompany,
        companyKey: "ndl",
        showYesNo: true,
      }),
    },
    {
      id: "MWG SIGN POST",
      show: isOfferMoneyWellness,
      content: (
        <>
          {renderSupportCompanyCard({
            title: "Money Wellness",
            subtitle: "Support from Money Wellness",
            description:
              "Appointment service to get free, one-to-one support from a specialist advisor. They’ll take the time to understand your situation and create a clear plan to help you manage your debts and improve your financial wellbeing.",
            bulletPoints: [
              "Tailored debt advice that fits your situation",
              "Non-judgmental guidance from trained experts",
              "Tools and tips to help you better manage your finances",
            ],
            showYesNo: false,
          })}

          <Divider sx={{ mt: 2, mb: 2.5 }} />

          <Typography data-testid="pages-private-dashboard-cards-text-action-3-mwg-sign-post"
            variant="h6"
            sx={{ fontWeight: 400, mb: 2, color: ThemeColours.subtitleVar2 }}
          >
            To learn more, please select from the available contact option(s)
            below.{" "}
            <span style={{ color: "red" }}>
              Note: You will only see the “Text me” option if you have entered a
              mobile number as part of the onboarding below.
            </span>
          </Typography>

          <Box data-testid="pages-private-dashboard-cards-container-action-2-mwg-sign-post" sx={{ display: "flex", gap: 1 }}>
            <Button data-testid="pages-private-dashboard-cards-btn-email-me-mwg-sign-post"
              variant="contained"
              onClick={() => {
                sendDebtAdvice("sendEmail");
              }}
              size="medium"
              sx={{
                borderRadius: 1,
                textTransform: "none",
                fontWeight: 400,
                py: 1,
                px: 3,
                boxShadow: "none",
                backgroundColor: ThemeColours.buttonVar1,
              }}
              disabled={disableMwgBtns}
            >
              Email Me
            </Button>
            {hasMobileNumber && (
              <Button data-testid="pages-private-dashboard-cards-btn-text-me-mwg-sign-post"
                variant="contained"
                size="medium"
                sx={{
                  borderRadius: 1,
                  textTransform: "none",
                  fontWeight: 400,
                  py: 1,
                  px: 3,
                  boxShadow: "none",
                  backgroundColor: ThemeColours.buttonVar1,
                }}
                onClick={() => {
                  sendDebtAdvice("sendSMS");
                }}
                disabled={disableMwgBtns}
              >
                Text Me
              </Button>
            )}
          </Box>
          <Box data-testid="pages-private-dashboard-cards-container-action-3-mwg-sign-post">
            {visible && (
              <Typography data-testid="pages-private-dashboard-cards-text-resend-in-mwg-sign-post"
                variant="h6"
                sx={{
                  color: "#6B7280",
                  fontWeight: 400,
                  fontSize: "14px",
                  lineHeight: "21px",
                }}
              >
                Resend in {count} {count === 1 ? "second" : "seconds"}.
              </Typography>
            )}
          </Box>
        </>
      ),
    },
    {
      id: "YES ENERGY",
      show: showYesEnergy,
      content: renderSupportCompanyCard({
        title: "YES Energy Solutions",
        subtitle: "Support from YES Energy Solutions",
        description:
          "Help to cut energy bills with simple, tailored guidance and access to the right support schemes.",
        bulletPoints: [
          "Tailored energy-saving advice",
          "Help to find better tariffs",
          "Support to reduce ongoing costs",
        ],
        description2:
          "With your consent we’ll share your budget with YES Energy Solutions so they can offer you support.",
        feeDisclosure:
          "This company provides this service free of charge. If you choose to use its service following our referral, IE Hub may receive a referral fee. You will not be charged for this referral.",
        selectedValue: selectedYe,
        handleShareCompany,
        companyKey: "ye",
        showYesNo: false,
      }),
    },
    {
      title: "YWCT - (YORKSHIRE WATER COMMUNITY TRUST)",
      show: showYWCTMessage,
      content: renderSupportCompanyCard({
        title: "Yorkshire Water Community Trust",
        subtitle: "Support from Yorkshire Water Community Trust",
        description:
          "Practical help for customers struggling with water bills, with schemes designed to make payments more manageable.",
        bulletPoints: [
          "Help with water bill debt",
          "Grants and payment support",
          "Quick, simple applications",
        ],
        description2:
          "With your consent we’ll share your budget with Yorkshire Water Community Trust so they can offer you support.",
        selectedValue: selectedYwct,
        handleShareCompany,
        companyKey: "ywct",
        showYesNo: false,
      }),
    },
    // { title: "ENW AS REFERRED COMPANY", show: showEnwReferredCard },
    {
      title: "SLS",
      show: isSLSEligible,
      content: renderSupportCompanyCard({
        title: "Illegal Money Lending Team (Stop Loan Sharks)",
        subtitle: "Support from Illegal Money Lending Team (Stop Loan Sharks)",
        description:
          "Confidential emotional and practical support to anyone affected by illegal money lending. The Stop Loan Sharks team can help keep you safe and take action against loan sharks.",
        bulletPoints: [
          "Free and confidential support if you’ve borrowed from a loan shark",
          "Emotional and practical help from trained staff",
          "Tailored support to keep you safe and in control",
        ],
        description2:
          "With your consent we’ll share your budget with Illegal Money Lending Team (Stop Loan Sharks) so they can offer you support.",
        selectedValue: selectedSls,
        handleShareCompany,
        companyKey: "sls",
        showYesNo: true,
      }),
    },
    // { title: "ENW RECOMMENDATION", show: showEnwRecommendationCard },
    // { title: "SIGNPOST+RECOMMENDATION", show: showSignpostRecommendationCard },
    {
      title: "Vulnerability Registration Service",
      show: hasVulnerabilityRegistrationService,
      content: renderSupportCompanyCard({
        title: "VRS",
        subtitle: "Support from VRS",
        description:
          "Free service that helps organisations understand when people need extra support, so they can treat them with care and understanding.",
        bulletPoints: [
          "Register your circumstances once",
          "Get tailored support when dealing with companies",
          "Free, secure and trusted service",
        ],
        description2:
          "With your consent we’ll share your budget with VRS so they can alert companies of your vulnerable circumstances.",
        selectedValue: selectedVrs,
        handleShareCompany,
        companyKey: "vrs",
        showYesNo: true,
      }),
    },
    {
      title: "MLB (Make life better)",
      show: showMLBCard,
      content: renderSupportCompanyCard({
        title: "Make Life Better",
        subtitle: "Support from Make Life Better",
        description:
          "Help for families in financial hardship to unlock the benefits, grants and discounts they’re entitled to - without the stress of navigating complex systems.",
        bulletPoints: [
          "Support finding unclaimed benefits and grants",
          "Help accessing discounts and financial support",
          "Easy, friendly guidance throughout the process",
        ],
        description2:
          "With your consent we’ll share your budget with Make Life Better so they can offer you support.",
        selectedValue: selectedMlb,
        handleShareCompany,
        companyKey: "mlb",
        showYesNo: true,
        ctaText: `Would you like us to share your budget with Make Life Better? If you consent we'll also send you a link so you can book an appointment with the MLB team.`,
      }),
    },
    {
      title: "Income max",
      // Hope4U shows -> Income Max only if already on the share list. TODO: move to backend.
      // To revert, use: show: showIncomeMaxCard,
      show:
        showIncomeMaxCard &&
        (!showHope4UCard ||
          customerShareData.some(
            (e) => e.lenderName === lenderNameMap.im && e.shareNow === "Y" && e.isDeleted !== "Y",
          )),
      content: renderSupportCompanyCard({
        title: "Income Max",
        subtitle: "Support from Income Max",
        description:
          "Help to identify and claim benefits and grants you’re entitled to, boosting household income.",
        bulletPoints: [
          "Personalised benefits check",
          "Help with claims and forms",
          "Support to boost household income",
        ],
        description2:
          "With your consent we’ll share your budget with Income Max so they can offer you support.",
        selectedValue: selectedIm,
        handleShareCompany,
        companyKey: "im",
        showYesNo: true,
        ctaText: `Would you like us to share your budget with Income Max to receive help making a claim?`,
      }),
    },
    {
      title: "CadentGas",
      show: isCadentEligible,
      content: renderSupportCompanyCard({
        title: "Free Gas Safety Check and Servicing Available",
        titleMeta: "Cadent",
        subtitle:
          "You may be entitled to free support to help keep your home safe and warm.",
        description:
          "Based on your current situation, an engineer can come to your property to carry out free gas appliance safety checks and services.\n This support is provided through Cadent’s ‘Services Beyond the Meter’ programme, which helps people who may be experiencing financial hardship.",
        bulletPoints: [
          "Free gas appliance safety checks and support",
          "Help for customers facing financial vulnerability",
          "Tailored help to make your home safer and more efficient",
        ],
        description2:
          "With your consent we’ll share your budget with Services Beyond the Meter (Cadent) so they can offer you support.",
        selectedValue: selectedCg,
        handleShareCompany,
        companyKey: "cg",
        showYesNo: true,
      }),
    },
    {
      title: "Additional Support Partners",
      show: supportPartnersNames.length > 0,
      content: (
        <Box data-testid="pages-private-dashboard-cards-container-action-4-asp">
          <Typography data-testid="pages-private-dashboard-cards-text-additional-support-partners-asp" variant="h5" sx={{ fontWeight: 500, mb: 1 }}>
            Additional Support Partners{" "}
          </Typography>
          <Box data-testid="pages-private-dashboard-cards-container-action-5-asp" sx={{ mb: 1 }}>
            <Typography data-testid="pages-private-dashboard-cards-text-action-4-asp"
              variant="h6"
              sx={{ fontWeight: 400, mb: 1, color: ThemeColours.bodyVar1 }}
            >
              We work with trusted partners who might offer you additional
              support. Besides the companies you're sharing your budget with,
              you can also include others from the list below.
            </Typography>
          </Box>

          <Box data-testid="pages-private-dashboard-cards-container-action-6-asp" sx={{ mb: 2 }}>
            <Typography data-testid="pages-private-dashboard-cards-text-action-5-asp"
              variant="h6"
              sx={{ fontWeight: 400, mb: 1, color: ThemeColours.subtitleVar3 }}
            >
              Do you wish to share your budget with any of our support partners?
            </Typography>
          </Box>
          <Box data-testid="pages-private-dashboard-cards-container-action-7-asp">
            <FormControl sx={{ minWidth: "100%" }}>
              <Autocomplete
                multiple
                options={supportPartnersNames}
                onOpen={handleAutocompleteClick}
                value={selectedSupportPartners}
                onChange={(_event, newValue) =>
                  setSelectedSupportPartners(newValue)
                }
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip data-testid={`pages-private-dashboard-cards-chip-${option}`}
                      label={option}
                      {...getTagProps({ index })}
                      sx={{ backgroundColor: "#F2F5F6" }}
                    />
                  ))
                }
                renderInput={(params) => {
                  const { size, InputLabelProps, ...rest } = params;
                  const safeInputLabelProps = InputLabelProps
                    ? {
                        className: InputLabelProps.className ?? "",
                        style: InputLabelProps.style ?? {},
                      }
                    : null;

                  return (
                    <TextField data-testid="pages-private-dashboard-cards-input-action-asp"
                      {...rest}
                      {...(size ? { size } : {})}
                      {...(safeInputLabelProps
                        ? { InputLabelProps: safeInputLabelProps }
                        : {})}
                      variant="outlined"
                      placeholder={
                        selectedSupportPartners.length === 0
                          ? "Select company(ies)"
                          : ""
                      }
                      InputProps={{
                        ...params.InputProps,
                        readOnly: true,
                        sx: {
                          padding: 0,
                          cursor: "pointer",
                          "& .MuiInputBase-input": {
                            cursor: "pointer",
                          },
                        },
                      }}
                      sx={{
                        cursor: "pointer",
                        "& .MuiOutlinedInput-root": {
                          cursor: "pointer",
                        },
                      }}
                    />
                  );
                }}
                disableCloseOnSelect={false}
                sx={{
                  width: "100%",
                  borderRadius: 3,
                  "& .MuiAutocomplete-inputRoot": {
                    padding: 0.5,
                  },
                }}
              />

              <div style={{ display: "none" }}>
                <ThemeSelect
                  ref={themeSelectRef}
                  dialogLabel="Select a Company"
                  options={[
                    ...supportPartnersNames.map((creditor) => ({
                      value: creditor,
                      label: creditor,
                    })),
                  ]}
                  value={selectedSupportPartners}
                  onChange={(value) =>
                    setSelectedSupportPartners(
                      Array.isArray(value) ? value.map(String) : [],
                    )
                  }
                  placeholder="Select a Company"
                  showErrorBelow={false}
                  searchable={true}
                  fullWidth
                  multiple
                  logAction={{
                    pageName: "AdditionalSupport",
                    subSection: "ASP",
                  }}
                />
              </div>
            </FormControl>
          </Box>
        </Box>
      ),
    },
  ];

  const showCardsList = useMemo(() => {
    return cards.some((card) => card.show === true);
  }, [cards]);

  return (
     <Box data-testid="pages-private-dashboard-additional-support-modal-container-action-1" sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Dialog Title */}
      <DialogTitle data-testid="pages-private-dashboard-additional-support-modal-header-action"
        sx={{
          px: 3,
          justifyContent: "space-between",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Typography data-testid="pages-private-dashboard-additional-support-modal-text-additional-support-options" variant="h5" component="span" sx={{ fontWeight: 500 }}>
          Additional Support Options
        </Typography>

        <IconButton data-testid="pages-private-dashboard-additional-support-modal-btn-handle-save-1"
          aria-label="close"
          onClick={handleSave}
          sx={{
            color: "#9ca3af",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider sx={{ marginLeft: 3, marginRight: 4 }} />

      {/* Dialog Content */}
      <DialogContent
        ref={contentRef}
        sx={{
          pt: 3,
          px: 3,
          flexGrow: 1,
          overflowY: "auto",
          position: "relative",
        }}
      >
        {aspLoaded() && (
          <Box data-testid="pages-private-dashboard-additional-support-modal-container-action-2" sx={{ pt: 0 }}>
            <Typography data-testid="pages-private-dashboard-additional-support-modal-text-action-1" variant="body1" sx={{ mb: 2 }}>
              Based on your budget and what you've told us, we've found some
              support services that might help you.
            </Typography>

            <Box data-testid="pages-private-dashboard-additional-support-modal-container-action-3" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {cards.map(
                (card, index) =>
                  card.show && (
                    <Card data-testid={`pages-private-dashboard-additional-support-modal-container-action-4-${index}`}
                      key={index}
                      sx={{
                        width: "100%",
                        boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                        border: "1px solid #e0e0e0",
                        borderRadius: 3,
                      }}
                    >
                      <CardContent>
                        {card.content ? (
                          card.content
                        ) : (
                          <Typography data-testid={`pages-private-dashboard-additional-support-modal-text-action-2-${(card.id || card.title || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} variant="subtitle1">
                            {card.title}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  ),
              )}
            </Box>
          </Box>
        )}

        {!aspLoaded() && (
          <Box data-testid="pages-private-dashboard-additional-support-modal-container-action-5"
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "80%",
            }}
          >
            <CircularProgress />
          </Box>
        )}

        <Box data-testid="pages-private-dashboard-additional-support-modal-container-action-6" sx={{ minHeight: 75 }}></Box>
      </DialogContent>

      {openDetailsKey && SUPPORT_DETAILS[openDetailsKey] && (
        <SupportDetailsPanel
          open
          onClose={() => setOpenDetailsKey(null)}
          content={SUPPORT_DETAILS[openDetailsKey]}
          testIdPrefix={`pages-private-dashboard-additional-support-modal-${openDetailsKey}`}
        />
      )}

      <DialogActions
        sx={{
          px: 4,
          py: 2,
          borderTop: "1px solid #e0e0e0",
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: "white",
          zIndex: 1,
          boxShadow: "0 -4px 6px rgba(0, 0, 0, 0.1)",
          opacity: isAtBottom ? 1 : 0,
          pointerEvents: isAtBottom ? "auto" : "none",
          transition: "opacity 0.2s",
          maxHeight: 75,
        }}
      >
        <Button data-testid="pages-private-dashboard-additional-support-modal-btn-handle-save-2"
          variant="contained"
          onClick={handleSave}
          size="large"
          sx={{
            borderRadius: 1,
            textTransform: "none",
            fontWeight: 500,
            py: 1.3,
            px: 3.5,
            boxShadow: "none",
            backgroundColor: ThemeColours.buttonVar1,
          }}
          disabled={!aspLoaded() || disableContinue}
        >
          Continue
        </Button>
      </DialogActions>
    </Box>
  );
};

export default AdditionalSupportModal;
